import { useState, useEffect } from "react";
import { useMutation } from "react-query";
import { useTranslation } from "react-i18next";
import { BaseDialog } from "./dialog";
import { Button } from "../ui/button";
import { InputWithAlert } from "../ui/input-with-alert";
import { SelectWithAlert } from "../ui/select-with-alert";
import { DatePicker } from "../ui/date-picker";
import { CONTRACT_TAX_DEDUCTIBLES, CONTRACT_PITS, SUPPORTED_CURRENCIES } from "@/constants";
import { calculateCurrencyUD, getRateToPLN } from "@/service/weexpertService";
import { toast } from "../ui/use-toast";
import { Loader2 } from "lucide-react";
import { format } from "date-fns";

export const CurrencyCalculatorPopup = ({ 
  defaultTaxDeductible = null, 
  defaultPit = null,
  currency: defaultCurrency = "PLN",
  currencyDate: defaultCurrencyDate = null,
  currentNbpRate = "",
  className = "" 
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  
  const [amount, setAmount] = useState("");
  const [taxDeductible, setTaxDeductible] = useState(defaultTaxDeductible || "ZERO_PERCENT");
  const [pit, setPit] = useState(defaultPit || "ZERO_PERCENT");
  const [additionalFeePln, setAdditionalFeePln] = useState("");
  const [additionalFeeCurrency, setAdditionalFeeCurrency] = useState("");
  const [currency, setCurrency] = useState(defaultCurrency);
  const [currencyDate, setCurrencyDate] = useState(defaultCurrencyDate);
  const [npbRate, setNpbRate] = useState(currentNbpRate || "");
  const [comertialBankRate, setComertialBankRate] = useState("");
  const [results, setResults] = useState(null);
  const [isLoadingRate, setIsLoadingRate] = useState(false);

  // NBP rate fetching effect - similar to bill form logic
  useEffect(() => {
    if (currency === 'PLN') {
      setNpbRate("1.00");
      return;
    }
    
    if (currencyDate && currency !== 'PLN') {
      setIsLoadingRate(true);
      getRateToPLN(currency, format(currencyDate, "yyyy-MM-dd"))
        .then(res => {
          setNpbRate(res.data.midRate);
        })
        .catch((e) => {
          console.log(e);
          toast({
            variant: "destructive",
            title: t("Error"),
            description: t("error_fetching_currency_rate")
          });
        })
        .finally(() => {
          setIsLoadingRate(false);
        });
    } else {
      setNpbRate('');
    }
  }, [currency, currencyDate, t]);

  // Update state when props change (when user changes currency in bill form)
  useEffect(() => {
    setCurrency(defaultCurrency);
    setCurrencyDate(defaultCurrencyDate);
    setNpbRate(currentNbpRate || "");
  }, [defaultCurrency, defaultCurrencyDate, currentNbpRate]);

  const calculateMutation = useMutation({
    mutationFn: () => calculateCurrencyUD(
      parseFloat(amount),
      taxDeductible,
      pit,
      parseFloat(additionalFeePln || 0),
      parseFloat(additionalFeeCurrency || 0),
      parseFloat(npbRate),
      parseFloat(comertialBankRate)
    ),
    onSuccess: (response) => {
      setResults(response.data.calculation);
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: t("Error"),
        description: error.response?.data?.message || t("calculation_error")
      });
    }
  });

  const handleCalculate = () => {
    if (!amount || !npbRate || !comertialBankRate) {
      toast({
        variant: "destructive",
        title: t("validation_error"),
        description: t("please_fill_required_fields")
      });
      return;
    }
    
    if (currency !== 'PLN' && !currencyDate) {
      toast({
        variant: "destructive",
        title: t("validation_error"),
        description: t("currency_date_required")
      });
      return;
    }
    
    calculateMutation.mutate();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pl-PL', { 
      minimumFractionDigits: 2,
      maximumFractionDigits: 2 
    }).format(value);
  };

  return (
    <BaseDialog
      dialogTitle={t("currency_calculator")}
      buttonText={t("currency_calculator")}
      open={open}
      setOpen={setOpen}
      className={className}
    >
      <div className="w-[600px]">
        <div className="space-y-4">
          <SelectWithAlert
            label={t("invoices:currency") + " *"}
            options={SUPPORTED_CURRENCIES}
            value={currency}
            onChangeValue={setCurrency}
            inputClassName="h-12 bg-white"
          />
          
          <DatePicker
            label={t("invoices:payment_currency_date") + (currency !== 'PLN' ? " *" : "")}
            date={currencyDate}
            defaultMonth={currencyDate}
            disabled={currency === 'PLN' ? true : { after: new Date() }}
            error={currency && currency !== 'PLN' && !currencyDate}
            errorMessage={t("field_required")}
            setDate={(date) => {
              if (!date) return;
              setCurrencyDate(date);
            }}
            inputClassName="h-12 bg-white"
            className={currency === 'PLN' ? 'opacity-50' : ''}
          />
          
          <InputWithAlert
            label={t("amount") + " *"}
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            error={!amount && calculateMutation.isLoading}
            errorMessage={t("field_required")}
            inputClassName="h-12 bg-white"
          />
          
          <SelectWithAlert
            label={t("contracts:tax_deductible")}
            options={CONTRACT_TAX_DEDUCTIBLES(t)}
            value={taxDeductible}
            onChangeValue={setTaxDeductible}
            inputClassName="h-12 bg-white"
          />
          
          <SelectWithAlert
            label="PIT"
            options={CONTRACT_PITS(t)}
            value={pit}
            onChangeValue={setPit}
            inputClassName="h-12 bg-white"
          />
          
          <InputWithAlert
            label={t("additional_fee_pln")}
            type="number"
            value={additionalFeePln}
            onChange={(e) => setAdditionalFeePln(e.target.value)}
            placeholder="0.00"
            inputClassName="h-12 bg-white"
          />
          
          <InputWithAlert
            label={t("additional_fee_currency")}
            type="number"
            value={additionalFeeCurrency}
            onChange={(e) => setAdditionalFeeCurrency(e.target.value)}
            placeholder="0.00"
            inputClassName="h-12 bg-white"
          />
          
          <div>
            <InputWithAlert
              label={t("nbp_rate") + " * " + t("auto_fetched")}
              type="number"
              value={npbRate}
              onChange={(e) => setNpbRate(e.target.value)}
              placeholder="0.0000"
              error={!npbRate && calculateMutation.isLoading}
              errorMessage={t("field_required")}
              inputClassName="h-12 bg-white"
              disabled={isLoadingRate}
            />
            {isLoadingRate && (
              <div className="flex items-center mt-1 text-sm text-gray-500">
                <Loader2 className="h-3 w-3 animate-spin mr-1" />
                {t("fetching_rate")}
              </div>
            )}
          </div>
          
          <InputWithAlert
            label={t("commercial_bank_rate") + " *"}
            type="number"
            value={comertialBankRate}
            onChange={(e) => setComertialBankRate(e.target.value)}
            placeholder="0.0000"
            error={!comertialBankRate && calculateMutation.isLoading}
            errorMessage={t("field_required")}
            inputClassName="h-12 bg-white"
          />
        </div>
        
        <div className="mt-6 flex justify-center">
          <Button 
            onClick={handleCalculate}
            disabled={calculateMutation.isLoading}
            className="w-32"
          >
            {calculateMutation.isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              t("calculate")
            )}
          </Button>
        </div>

        {results && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold mb-3">{t("calculation_results")}:</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>{t("send_brutto")}:</span>
                <span className="font-medium">{formatCurrency(results.sendBrutto)}</span>
              </div>
              <div className="flex justify-between">
                <span>{t("send_netto_currency")}:</span>
                <span className="font-medium">{formatCurrency(results.sendNettoCurrency)}</span>
              </div>
              <div className="flex justify-between">
                <span>{t("difference_currency_pit")}:</span>
                <span className="font-medium">{formatCurrency(results.differenceCurrencyInPit)}</span>
              </div>
              <div className="flex justify-between">
                <span>{t("initial_pit")}:</span>
                <span className="font-medium">{formatCurrency(results.initialPit)}</span>
              </div>
              <div className="flex justify-between">
                <span>{t("pln_for_bill")}:</span>
                <span className="font-medium">{formatCurrency(results.plnForBill)} PLN</span>
              </div>
              <div className="flex justify-between">
                <span>{t("adjusted_pit")}:</span>
                <span className="font-medium">{formatCurrency(results.adjustedPit)}</span>
              </div>
              <div className="flex justify-between">
                <span>{t("pit_buy")}:</span>
                <span className="font-medium">{formatCurrency(results.pitBuy)}</span>
              </div>
              <div className="flex justify-between">
                <span>{t("fees")}:</span>
                <span className="font-medium">{formatCurrency(results.fees)}</span>
              </div>
              {results.plnFeeConverted && (
                <div className="flex justify-between">
                  <span>{t("pln_fee_converted")}:</span>
                  <span className="font-medium">{formatCurrency(results.plnFeeConverted)}</span>
                </div>
              )}
              <div className="flex justify-between border-t pt-2 mt-2">
                <span className="font-semibold">{t("total_fees")}:</span>
                <span className="font-semibold">{formatCurrency(results.totalFees)}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </BaseDialog>
  );
};